import React, { useState } from 'react';
import { LuCheck, LuCircleHelp, LuX } from 'react-icons/lu';
import './Groups.css';
import Reveal from '../shared/Reveal';
import SampleTag from '../shared/SampleTag';

// Status names follow AttendanceSheet.jsx: Completed is shown as Present,
// Declined as Absent, Assigned as Pending. `code` is the CSS class suffix, so
// the legend cannot derive it from the label (Pending and Present both start P).
const STATUS = {
    P: { code: 'P', label: 'Present', Icon: LuCheck },
    A: { code: 'A', label: 'Absent', Icon: LuX },
    W: { code: 'W', label: 'Pending', Icon: LuCircleHelp }
};

// Click order, matching how AttendanceSheet cycles a cell.
const CYCLE = ['P', 'A', 'W'];

const SESSIONS = ['Mon', 'Wed', 'Fri', 'Mon', 'Wed'];

const ROSTER = [
    { name: 'Eleni Karagianni', marks: 'PPPPP' },
    { name: 'Nikos Papadakis', marks: 'PPAPP' },
    { name: 'Matteo Ricci', marks: 'PPPAP' },
    { name: 'Katerina Vlachou', marks: 'APPPW' },
    { name: 'Yannis Oikonomou', marks: 'PPPPW' },
    { name: 'Ioanna Stamou', marks: 'PAPPW' },
    { name: 'Luca Ferrante', marks: 'PPPPP' }
];

export default function Groups() {
    // The copy promises a click on a cell updates it, so the sheet is live.
    const [roster, setRoster] = useState(ROSTER);

    const cycleMark = (athleteIndex, sessionIndex) => {
        setRoster(prev =>
            prev.map((athlete, i) => {
                if (i !== athleteIndex) return athlete;
                const marks = athlete.marks.split('');
                const next = CYCLE[(CYCLE.indexOf(marks[sessionIndex]) + 1) % CYCLE.length];
                marks[sessionIndex] = next;
                return { ...athlete, marks: marks.join('') };
            })
        );
    };

    return (
        <section id='groups' className='lp-section lp-groups lp-tint'>
            <div className='lp-wrap'>
                <Reveal>
                    <p className='lp-eyebrow'>Groups and attendance</p>
                    <h2 className='lp-h2'>A squad, and who showed up.</h2>
                    <p className='lp-lede'>
                        A coach creates a group, adds athletes and schedules workouts. The
                        attendance sheet lists every athlete against every session, and a click on
                        a cell updates it.
                    </p>
                </Reveal>

                <Reveal className='lp-roster' delay={100}>
                    <div className='lp-roster-head'>
                        <h3>Group sheet</h3>
                        <SampleTag>Sample data</SampleTag>
                    </div>
                    <div className='lp-table-wrap'>
                        <table className='lp-table lp-roster-table'>
                            <caption className='lp-visually-hidden'>
                                Sample attendance for seven athletes over five sessions.
                                Select a cell to change that athlete's status for the session.
                            </caption>
                            <thead>
                                <tr>
                                    <th scope='col'>Athlete</th>
                                    {SESSIONS.map((day, index) => (
                                        <th scope='col' key={index} className='lp-roster-cell'>
                                            {day}
                                        </th>
                                    ))}
                                    <th scope='col'>Attendance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {roster.map((athlete, athleteIndex) => {
                                    const marks = athlete.marks.split('');
                                    const present = marks.filter(mark => mark === 'P').length;
                                    return (
                                        <tr key={athlete.name}>
                                            <th scope='row'>{athlete.name}</th>
                                            {marks.map((mark, sessionIndex) => {
                                                const { label, Icon } = STATUS[mark];
                                                const next = STATUS[CYCLE[(CYCLE.indexOf(mark) + 1) % CYCLE.length]];
                                                return (
                                                    <td key={sessionIndex} className='lp-roster-cell'>
                                                        <button
                                                            type='button'
                                                            className={`lp-mark lp-mark-${mark} lp-mark-btn`}
                                                            onClick={() => cycleMark(athleteIndex, sessionIndex)}
                                                            /* title is not reliably exposed
                                                               to screen readers, so the
                                                               full context is in the
                                                               accessible name. */
                                                            aria-label={`${athlete.name}, ${SESSIONS[sessionIndex]}: ${label}. Change to ${next.label}`}
                                                        >
                                                            <Icon aria-hidden='true' />
                                                            <span className='lp-visually-hidden'>{label}</span>
                                                        </button>
                                                    </td>
                                                );
                                            })}
                                            <td className='lp-roster-total'>
                                                {/* Plain text, not BallisticNumber:
                                                    that component writes
                                                    textContent, which React does
                                                    not track, so a changing value
                                                    renders stale until the next
                                                    animation ends. */}
                                                <span className='lp-num'>{present}</span>
                                                <span className='lp-roster-of'> of {marks.length}</span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <ul className='lp-legend'>
                        {Object.values(STATUS).map(({ code, label, Icon }) => (
                            <li key={code}>
                                <span className={`lp-mark lp-mark-${code}`}>
                                    <Icon aria-hidden='true' />
                                </span>
                                {label}
                            </li>
                        ))}
                    </ul>
                    <p className='lp-roster-hint'>
                        Select any badge to move it to the next status.
                    </p>
                </Reveal>
            </div>
        </section>
    );
}
